import { readFileSync } from 'fs'
import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs, collectMulti } from './context/args'
import { emitJSON } from './context/render'
import { checkSecretsOrExit, isAllowSecrets } from './context/validation'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'
import { normalizeProjectPlan, runProjectApply, type ApplyApi } from './project-apply'
import { CARD_STATUSES as CARD_STATUS_VALUES, PROJECT_STATUSES as PROJECT_STATUS_VALUES } from '@driftless/topic-schema'

// Projects + Cards CLI — the fourth primitive. A project is a shared execution
// environment; cards are its atomic units of work. Reads are direct API calls;
// the agent's default card order (blocked → review → in_progress → todo → done)
// is enforced server-side.

// Re-export the shared vocabularies, widened to readonly string[] so the CLI can
// validate arbitrary user input with .includes() (the lib's `as const` narrows it).
export const CARD_STATUSES: readonly string[] = CARD_STATUS_VALUES
export const PROJECT_STATUSES: readonly string[] = PROJECT_STATUS_VALUES

const KNOWN_PROJECT_FLAGS: ReadonlySet<string> = new Set([
  'status', 'limit', 'cursor', 'title', 'description', 'owner', 'acceptance', 'validate', 'dep', 'file', 'allow-secrets', 'json', 'help', 'goal', 'playbook', 'dry-run',
])

// Opaque offset cursor for `project cards` paging of a large project. base64url
// {o:offset}; a malformed cursor resets to 0 (degrades to first page, never errors).
export function encodeCardsCursor(offset: number): string {
  return Buffer.from(JSON.stringify({ o: offset })).toString('base64url')
}
export function decodeCardsCursor(raw: unknown): number {
  if (typeof raw !== 'string' || raw.trim() === '') return 0
  try {
    const o = Number((JSON.parse(Buffer.from(raw, 'base64url').toString('utf8')) as { o?: unknown })?.o)
    return Number.isInteger(o) && o >= 0 ? o : 0
  } catch {
    return 0
  }
}

export function assertKnownProjectFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_PROJECT_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_PROJECT_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless project':\n` +
      lines.join('\n') +
      `\nRun 'driftless project --help' for valid flags.`,
  )
}

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(`driftless project <subcommand> [args] [flags]

Projects — a shared execution environment for humans and agents. A project is a
board of cards; each card is one unit of work. The product lives in CLI + MCP;
the dashboard is occasional visibility for the human.

The loop (repeat until the board is done):
  1. project list                          See the boards, pick one
  2. project card next <project-id>        Get the next actionable card + its context bundle
  3. work the card, then run its validate command
  4. project card status <id> <card> done  Mark it done — then go back to step 2

Projects:
  project add <title> [--goal "..."] [--playbook <slug>]   Create a project
  project list [--status active|done|archived] List projects
  project get <id>                             Project detail + card index
  project update <id> [--status active|done|archived] [--title "..."] [--goal "..."] [--playbook <slug>]   Update a project
  project apply <id> --file @plan.json [--dry-run] [--json]   Declaratively apply a whole plan (goal + cards + deps), idempotent

Cards:
  project cards <project-id> [--status s] [--owner id] [--limit n] [--cursor c]   List cards (priority order, bounded; page with --cursor) — NOT for the loop
  project card next <project-id>               Next actionable card + context bundle (loop tick — use THIS to work a 50k-card project, not cards)
  project card add <project-id> --title "..." [--description "..."|@file] [--acceptance "..."] [--validate "cmd"] [--dep <card-id>] [--owner <email|member-id|user-id>]
  project card add <project-id> --file @cards.json     Batch-create cards (array; depends_on by title or id)
  project card update <project-id> <card-id> [--title] [--description "..."|@file] [--acceptance] [--validate] [--dep <card-id>] [--status] [--owner <email|member-id|user-id>]
  project card get <project-id> <card-id>      Card detail
  project card status <project-id> <card-id> <status>   Change a card's status
  project card move <project-id> <card-id> <to-project-id>   Move a card to another project
  project card rm <project-id> <card-id>       Delete a card

Card statuses: ${CARD_STATUSES.join(' · ')}
Flags: --json (machine output) · --allow-secrets (override the secret scan)
       --acceptance "..." · --validate "cmd" · --dep <card-id> (repeatable, sets depends_on)
       --description accepts @file (reads the file as the body) · --file @cards.json batch-creates
`)
}

export function statusList(raw: string | boolean | undefined): string | undefined {
  if (typeof raw !== 'string' || !raw.trim()) return undefined
  return raw.split(',').map((s) => s.trim()).filter(Boolean).join(',')
}

// Resolve a flag value that may be an `@file` reference (parity with `context --content`).
// A bare string passes through; `@path` reads the file as the value.
export function readMaybeFile(raw: string | boolean | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined
  if (raw.startsWith('@')) {
    try { return readFileSync(raw.slice(1), 'utf-8') }
    catch { console.error(`File not found: ${raw.slice(1)}`); process.exit(1) }
  }
  return raw
}

export interface BatchCardInput {
  title: string
  description?: string
  acceptance?: string
  validate?: string
  status?: string
  owner?: string
  depends_on?: string[]
}

// Parse a batch-cards file body into a validated, ordered list. Accepts a bare JSON
// array or `{ cards: [...] }`. Throws a string message on any structural problem (the
// caller turns it into a CLI error). `depends_on` entries may be titles (resolved to
// ids at creation time, referencing cards defined EARLIER in the list) or raw UUIDs.
export function normalizeBatchCards(parsed: unknown): BatchCardInput[] {
  const cards: unknown = Array.isArray(parsed)
    ? parsed
    : (parsed && typeof parsed === 'object' && Array.isArray((parsed as { cards?: unknown }).cards))
      ? (parsed as { cards: unknown[] }).cards
      : null
  if (!cards) throw 'file must be a JSON array of cards, or an object { "cards": [...] }'
  if (!(cards as unknown[]).length) throw 'file contains no cards'
  return (cards as unknown[]).map((raw, i): BatchCardInput => {
    if (!raw || typeof raw !== 'object') throw `card #${i + 1} is not an object`
    const c = raw as Record<string, unknown>
    if (typeof c.title !== 'string' || !c.title.trim()) throw `card #${i + 1} is missing a title`
    if (c.status !== undefined && !CARD_STATUSES.includes(c.status as string)) throw `card #${i + 1} has invalid status "${String(c.status)}"`
    if (c.depends_on !== undefined && !Array.isArray(c.depends_on)) throw `card #${i + 1} depends_on must be an array`
    return {
      title: c.title,
      description: typeof c.description === 'string' ? c.description : undefined,
      acceptance: typeof c.acceptance === 'string' ? c.acceptance : undefined,
      validate: typeof c.validate === 'string' ? c.validate : undefined,
      status: typeof c.status === 'string' ? c.status : undefined,
      owner: typeof c.owner === 'string' ? c.owner : undefined,
      depends_on: Array.isArray(c.depends_on) ? (c.depends_on as unknown[]).map(String) : undefined,
    }
  })
}

export async function projectCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }
  assertKnownProjectFlags(flags)

  // Validate required positional args before workspace resolution so
  // missing-arg errors surface ahead of auth errors in unconfigured envs.
  if ((sub === 'get' || sub === 'update' || sub === 'apply') && !positional[1]) {
    console.error(`Error: 'driftless project ${sub}' requires a project id.`)
    console.error(`Usage: driftless project ${sub} <project-id>`)
    process.exit(1)
  }
  if (sub === 'add' && !positional.slice(1).join(' ').trim()) {
    console.error('Error: a project title is required.')
    console.error('Usage: driftless project add "<title>" [--goal "..."] [--playbook <slug>]')
    process.exit(1)
  }

  const ws = await resolveWorkspaceSlug()
  const base = `/workspaces/${ws}/projects`

  try {
    switch (sub) {
      case 'add': {
        const title = positional.slice(1).join(' ').trim()
        if (!title) { console.error('Error: a project title is required.'); process.exit(1) }
        const body: Record<string, unknown> = { title }
        if (typeof flags['goal'] === 'string') body['goal'] = flags['goal']
        if (typeof flags['playbook'] === 'string') body['playbook_slug'] = flags['playbook']
        const result = (await api.post(base, body)) as any
        analyticsEvent('project_added', ws, { has_goal: 'goal' in body, has_playbook: 'playbook_slug' in body })
        if (isJSON) emitJSON(result)
        else console.log(`Project '${result.title}' created — id ${result.id}`)
        break
      }
      case 'list': {
        const status = statusList(flags['status'])
        if (status && !status.split(',').every((s) => PROJECT_STATUSES.includes(s))) {
          console.error(`Error: --status must be one of: ${PROJECT_STATUSES.join(', ')}`); process.exit(1)
        }
        const q = status ? `?status=${encodeURIComponent(status)}` : ''
        const result = (await api.get(`${base}${q}`)) as any[]
        if (isJSON) { emitJSON(result); break }
        if (!result.length) { console.log('No projects yet. Create one with: driftless project add "<title>"'); break }
        for (const p of result) {
          const counts = Object.entries(p.card_counts || {}).map(([k, v]) => `${v} ${k}`).join(', ') || 'no cards'
          console.log(`${p.id}  [${p.status}]  ${p.title}  — ${counts}`)
        }
        break
      }
      case 'get': {
        const id = positional[1]
        if (!id) { console.error('Error: project id required.'); process.exit(1) }
        const result = (await api.get(`${base}/${encodeURIComponent(id)}`)) as any
        if (isJSON) { emitJSON(result); break }
        console.log(`${result.title}  [${result.status}]  (owner ${result.owner})`)
        for (const c of result.cards || []) console.log(`  ${c.status.padEnd(12)} ${c.title}  (${c.id})`)
        break
      }
      case 'update': {
        const id = positional[1]
        if (!id) { console.error('Error: project id required. Usage: driftless project update <id> [--status ...] [--title "..."] [--goal "..."] [--playbook <slug>]'); process.exit(1) }
        const body: Record<string, unknown> = {}
        if (typeof flags['title'] === 'string') body['title'] = flags['title']
        if (typeof flags['goal'] === 'string') body['goal'] = flags['goal']
        if (typeof flags['playbook'] === 'string') body['playbook_slug'] = flags['playbook']
        if (flags['status'] !== undefined) {
          const status = flags['status']
          if (typeof status !== 'string' || !PROJECT_STATUSES.includes(status)) {
            console.error(`Error: --status must be one of: ${PROJECT_STATUSES.join(', ')}`); process.exit(1)
          }
          body['status'] = status
        }
        if (Object.keys(body).length === 0) {
          console.error('Error: nothing to update — pass --status, --title, --goal and/or --playbook.'); process.exit(1)
        }
        const result = (await api.patch(`${base}/${encodeURIComponent(id)}`, body)) as any
        analyticsEvent('project_update', ws, { has_status: 'status' in body, has_title: 'title' in body, has_goal: 'goal' in body, has_playbook: 'playbook_slug' in body })
        if (isJSON) emitJSON(result)
        else console.log(`Project updated — ${result.title} [${result.status}]`)
        break
      }
      case 'cards': {
        const id = positional[1]
        if (!id) { console.error('Error: project id required.'); process.exit(1) }
        const status = statusList(flags['status'])
        // Bounded for a 50k-card project: status/owner filters + keyset paging.
        // Fetch limit+1 (the API returns a bare array) to flag has_more + mint a
        // cursor without changing the API shape. The loop tick stays `card next`.
        const limit = flags['limit'] ? Math.max(1, parseInt(String(flags['limit']), 10) || 20) : 20
        const offset = decodeCardsCursor(flags['cursor'])
        const params = new URLSearchParams()
        if (status) params.set('status', status)
        params.set('limit', String(limit + 1))
        if (offset > 0) params.set('offset', String(offset))
        if (typeof flags['owner'] === 'string' && flags['owner'].trim() !== '') params.set('owner', flags['owner'].trim())
        const rows = (await api.get(`${base}/${encodeURIComponent(id)}/cards?${params.toString()}`)) as any[]
        const hasMore = rows.length > limit
        const cards = rows.slice(0, limit)
        const nextCursor = hasMore ? encodeCardsCursor(offset + limit) : null
        if (isJSON) { emitJSON({ cards, has_more: hasMore, nextCursor }); break }
        if (!cards.length) { console.log(`No cards match. For the loop, use: project card next ${id}`); break }
        for (const c of cards) console.log(`${c.status.padEnd(12)} ${c.title}  (${c.id})`)
        if (nextCursor) console.log(`\n  more cards — next page: project cards ${id} --cursor ${nextCursor}`)
        break
      }
      case 'apply': {
        const id = positional[1]
        if (!id) { console.error('Error: project id required. Usage: driftless project apply <project-id> --file @plan.json [--dry-run] [--json]'); process.exit(1) }
        if (typeof flags['file'] !== 'string') { console.error('Error: --file @plan.json is required.'); process.exit(1) }
        const path = flags['file'].startsWith('@') ? flags['file'].slice(1) : flags['file']
        let parsed: unknown
        try { parsed = JSON.parse(readFileSync(path, 'utf-8')) }
        catch (e: any) { console.error(`Could not read/parse ${path}: ${e.message}`); process.exit(1) }
        let plan
        try { plan = normalizeProjectPlan(parsed) }
        catch (msg) { console.error(`Error: ${String(msg)}.`); process.exit(1) }
        // Secret-scan ALL card text + project goal/title BEFORE any mutation so a
        // bad plan never half-applies.
        const allow = isAllowSecrets(flags)
        checkSecretsOrExit({ title: plan.title, content: plan.goal }, allow)
        for (const c of plan.cards) checkSecretsOrExit({ title: c.title, content: [c.description, c.acceptance, c.validate].filter(Boolean).join('\n') || undefined }, allow)
        const dryRun = flags['dry-run'] === true
        const summary = await runProjectApply(api as unknown as ApplyApi, base, id, plan, { dryRun })
        analyticsEvent('project_apply', ws, { dry_run: dryRun, created: summary.created.length, updated: summary.updated.length })
        if (isJSON) { emitJSON(summary); break }
        console.log(`${dryRun ? 'DRY RUN — ' : ''}project apply: ${summary.created.length} created, ${summary.updated.length} updated, ${summary.unchanged.length} unchanged${summary.project.changed ? `, project ${summary.project.fields.join('+')} ${dryRun ? 'would change' : 'changed'}` : ''}`)
        for (const c of summary.created) console.log(`  ${dryRun ? 'create' : 'created'}  ${c.title}${c.id ? ` (${c.id})` : ''}`)
        for (const c of summary.updated) console.log(`  ${dryRun ? 'update' : 'updated'}  ${c.title} (${c.id})`)
        for (const w of summary.warnings) console.log(`  ⚠ ${w}`)
        break
      }
      case 'card': {
        await cardSubcommand(positional, flags, ws, base, isJSON, args)
        break
      }
      default:
        console.error(`Unknown project subcommand: '${sub}'. Run 'driftless project --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}

async function cardSubcommand(
  positional: string[],
  flags: Record<string, string | boolean>,
  ws: string,
  base: string,
  isJSON: boolean,
  args: string[],
): Promise<void> {
  const action = positional[1]
  const projectId = positional[2]
  if (!action || !['add', 'get', 'next', 'update', 'status', 'move', 'rm'].includes(action)) {
    console.error("Usage: driftless project card <add|get|next|update|status|move|rm> <project-id> ...")
    process.exit(1)
  }
  if (!projectId) { console.error('Error: project id required.'); process.exit(1) }
  const cardsBase = `${base}/${encodeURIComponent(projectId)}/cards`

  if (action === 'next') {
    const result = (await api.get(`${cardsBase}/next`)) as any
    if (isJSON) { emitJSON(result); return }
    if (result.project_done) { console.log('Project complete — no cards remaining.'); return }
    if (!result.card) { console.log('No actionable cards — all remaining cards are blocked or waiting on deps.'); return }
    const c = result.card
    console.log(`${c.status.padEnd(12)} ${c.title}  (${c.id})`)
    if (c.acceptance) console.log(`  acceptance: ${c.acceptance}`)
    if (c.validate) console.log(`  validate:   ${c.validate}`)
    if (c.depends_on?.length) console.log(`  depends_on: ${c.depends_on.join(', ')}`)
    if (result.context_bundle?.length) console.log(`  context:    ${result.context_bundle.map((r: any) => `${r.type}:${r.ref}`).join(', ')}`)
    return
  }

  if (action === 'add') {
    // Batch mode: --file @cards.json creates the whole list in one command.
    if (typeof flags['file'] === 'string') {
      await addCardsFromFile(flags['file'], cardsBase, flags, ws, isJSON)
      return
    }
    const title = flags['title'] as string | undefined
    if (!title || !title.trim()) { console.error('Error: --title is required.'); process.exit(1) }
    const description = readMaybeFile(flags['description'])
    const owner = flags['owner'] as string | undefined
    const acceptance = flags['acceptance'] as string | undefined
    const validate = flags['validate'] as string | undefined
    // --dep is repeatable; parseArgs keeps only the last value, so collect all.
    const deps = collectMulti(args, ['--dep'])
    const depends_on = deps.length ? deps : undefined
    const body: Record<string, unknown> = { title, description, owner_clerk_id: owner }
    if (acceptance !== undefined) body['acceptance'] = acceptance
    if (validate !== undefined) body['validate'] = validate
    if (depends_on !== undefined) body['depends_on'] = depends_on
    // Secret hygiene — scan title + description (description maps onto `content`).
    checkSecretsOrExit({ title, content: description }, isAllowSecrets(flags))
    if (isAllowSecrets(flags)) body['allow_secrets'] = true
    const result = (await api.post(cardsBase, body)) as any
    analyticsEvent('project_card_added', ws, { has_owner: !!owner })
    if (isJSON) emitJSON(result)
    else console.log(`Card '${result.title}' created — id ${result.id} [${result.status}]`)
    return
  }

  const cardId = positional[3]
  if (!cardId) { console.error('Error: card id required.'); process.exit(1) }

  if (action === 'update') {
    const body: Record<string, unknown> = {}
    if (typeof flags['title'] === 'string') body['title'] = flags['title']
    const description = readMaybeFile(flags['description'])
    if (description !== undefined) body['description'] = description
    if (typeof flags['acceptance'] === 'string') body['acceptance'] = flags['acceptance']
    if (typeof flags['validate'] === 'string') body['validate'] = flags['validate']
    if (typeof flags['owner'] === 'string') body['owner_clerk_id'] = flags['owner']
    // --dep is repeatable; collect all occurrences (parseArgs keeps only the last).
    const deps = collectMulti(args, ['--dep'])
    if (deps.length) body['depends_on'] = deps
    if (flags['status'] !== undefined) {
      const status = flags['status']
      if (typeof status !== 'string' || !CARD_STATUSES.includes(status)) {
        console.error(`Error: status must be one of: ${CARD_STATUSES.join(', ')}`); process.exit(1)
      }
      body['status'] = status
    }
    if (Object.keys(body).length === 0) {
      console.error('Error: nothing to update — pass --title / --description / --acceptance / --validate / --dep / --status / --owner.')
      process.exit(1)
    }
    checkSecretsOrExit(
      { title: typeof body['title'] === 'string' ? body['title'] as string : undefined, content: typeof body['description'] === 'string' ? body['description'] as string : undefined },
      isAllowSecrets(flags),
    )
    if (isAllowSecrets(flags)) body['allow_secrets'] = true
    const result = (await api.patch(`${cardsBase}/${encodeURIComponent(cardId)}`, body)) as any
    analyticsEvent('project_card_update', ws, { fields: Object.keys(body).length })
    if (isJSON) emitJSON(result)
    else console.log(`Card updated — ${result.title} [${result.status}]`)
    return
  }

  if (action === 'get') {
    const result = (await api.get(`${cardsBase}/${encodeURIComponent(cardId)}`)) as any
    if (isJSON) { emitJSON(result); return }
    console.log(`${result.title}  [${result.status}]  (owner ${result.owner ?? 'unassigned'})`)
    if (result.description) console.log(`\n${result.description}`)
    if (result.context?.length) console.log(`\ncontext: ${result.context.map((c: any) => `${c.type}:${c.ref}`).join(', ')}`)
    return
  }

  if (action === 'move') {
    const toProjectId = positional[4]
    if (!toProjectId) { console.error('Usage: driftless project card move <project-id> <card-id> <to-project-id>'); process.exit(1) }
    const result = (await api.patch(`${cardsBase}/${encodeURIComponent(cardId)}`, { to_project_id: toProjectId })) as any
    analyticsEvent('project_card_move', ws, {})
    if (isJSON) emitJSON(result)
    else console.log(`Card moved to project ${toProjectId} — ${result.title}`)
    return
  }

  if (action === 'rm') {
    const result = (await api.delete(`${cardsBase}/${encodeURIComponent(cardId)}`)) as any
    analyticsEvent('project_card_rm', ws, {})
    if (isJSON) emitJSON(result ?? { deleted: cardId })
    else console.log(`Card deleted — id ${cardId}`)
    return
  }

  // status
  const status = positional[4]
  if (!status || !CARD_STATUSES.includes(status)) {
    console.error(`Error: status must be one of: ${CARD_STATUSES.join(', ')}`); process.exit(1)
  }
  const result = (await api.patch(`${cardsBase}/${encodeURIComponent(cardId)}`, { status })) as any
  analyticsEvent('project_card_status', ws, { status })
  if (isJSON) emitJSON(result)
  else console.log(`Card moved to [${result.status}] — ${result.title}`)
}

// Batch-create cards from a JSON file (array or { cards: [...] }). Created in list
// order so depends_on-by-title resolves against cards defined earlier; a title that
// matches no earlier card is passed through verbatim (so raw UUIDs work too).
async function addCardsFromFile(
  fileRef: string,
  cardsBase: string,
  flags: Record<string, string | boolean>,
  ws: string,
  isJSON: boolean,
): Promise<void> {
  const path = fileRef.startsWith('@') ? fileRef.slice(1) : fileRef
  let parsed: unknown
  try { parsed = JSON.parse(readFileSync(path, 'utf-8')) }
  catch (e: any) { console.error(`Could not read/parse ${path}: ${e.message}`); process.exit(1) }
  let cards: BatchCardInput[]
  try { cards = normalizeBatchCards(parsed) }
  catch (msg) { console.error(`Error: ${String(msg)}.`); process.exit(1) }
  // Secret-scan the whole batch up front so a bad card never half-creates a board.
  const allow = isAllowSecrets(flags)
  for (const c of cards) checkSecretsOrExit({ title: c.title, content: c.description }, allow)
  const titleToId = new Map<string, string>()
  const created: any[] = []
  for (const c of cards) {
    const depends_on = c.depends_on?.map((d) => titleToId.get(d) ?? d)
    const body: Record<string, unknown> = { title: c.title }
    if (c.description !== undefined) body['description'] = c.description
    if (c.acceptance !== undefined) body['acceptance'] = c.acceptance
    if (c.validate !== undefined) body['validate'] = c.validate
    if (c.status !== undefined) body['status'] = c.status
    if (c.owner !== undefined) body['owner_clerk_id'] = c.owner
    if (depends_on !== undefined) body['depends_on'] = depends_on
    if (allow) body['allow_secrets'] = true
    const res = (await api.post(cardsBase, body)) as any
    titleToId.set(c.title, res.id)
    created.push(res)
    if (!isJSON) console.log(`  ok  ${c.title} — ${res.id} [${res.status}]`)
  }
  analyticsEvent('project_cards_batch_added', ws, { count: created.length })
  if (isJSON) emitJSON(created)
  else console.log(`Created ${created.length} cards.`)
}
